import { useState, useEffect } from 'react';
import axios from 'axios';
import DashboardLayout from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Plus, Trash2, Pencil } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Technicians() {
  const [technicians, setTechnicians] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [editingTech, setEditingTech] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    cost_per_hour: '',
    overtime_multiplier: '1.5',
    skills: []
  });
  const { user } = useAuth();
  const perms = user?.effective_permissions || [];
  const canSeeRates = perms.includes('technicians.rates');

  useEffect(() => {
    fetchTechnicians();
  }, []);

  const fetchTechnicians = async () => {
    try {
      const response = await axios.get(`${API}/technicians`);
      setTechnicians(response.data);
    } catch (error) {
      toast.error('Failed to load technicians');
    }
  };

  const openAddDialog = () => {
    setEditingTech(null);
    setFormData({ name: '', email: '', cost_per_hour: '', overtime_multiplier: '1.5', skills: [] });
    setShowDialog(true);
  };

  const openEditDialog = (tech) => {
    setEditingTech(tech);
    setFormData({
      name: tech.name,
      email: tech.email || '',
      cost_per_hour: (tech.cost_per_hour ?? '').toString(),
      overtime_multiplier: (tech.overtime_multiplier ?? 1.5).toString(),
      skills: tech.skills || []
    });
    setShowDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: formData.name,
        email: formData.email.trim() || null,  // Send null if empty
        cost_per_hour: parseFloat(formData.cost_per_hour),
        overtime_multiplier: formData.overtime_multiplier ? parseFloat(formData.overtime_multiplier) : 1.5,
        skills: formData.skills
      };
      
      if (editingTech) {
        await axios.patch(`${API}/technicians/${editingTech.id}`, payload);
        toast.success('Technician updated successfully');
      } else {
        await axios.post(`${API}/technicians`, payload);
        toast.success('Technician added successfully');
      }
      setShowDialog(false);
      setEditingTech(null);
      setFormData({ name: '', email: '', cost_per_hour: '', overtime_multiplier: '1.5', skills: [] });
      fetchTechnicians();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save technician');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this technician?')) return;
    try {
      await axios.delete(`${API}/technicians/${id}`);
      toast.success('Technician deleted');
      fetchTechnicians();
    } catch (error) {
      toast.error('Failed to delete technician');
    }
  };

  const canManage = user?.role === 'management';

  return (
    <DashboardLayout>
      <div data-testid="technicians-page" className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h3 className="text-2xl font-bold text-slate-900 mb-2">Technicians</h3>
            <p className="text-slate-600">Manage your technician roster</p>
          </div>
          {canManage && (
            <Button onClick={openAddDialog} data-testid="add-technician-button" className="bg-slate-900 hover:bg-slate-800">
              <Plus size={20} className="mr-2" />
              Add Technician
            </Button>
          )}
        </div>

        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingTech ? 'Edit Technician' : 'Add New Technician'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="technician-form">
              <div className="space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="tech-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email (optional)</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  data-testid="tech-email-input"
                />
              </div>
              {canSeeRates && (
              <>
              <div className="space-y-2">
                <Label htmlFor="cost_per_hour">Cost per Hour ($)</Label>
                <Input
                  id="cost_per_hour"
                  type="number"
                  step="0.01"
                  value={formData.cost_per_hour}
                  onChange={(e) => setFormData({ ...formData, cost_per_hour: e.target.value })}
                  required
                  data-testid="tech-cost-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="overtime_multiplier">Overtime Multiplier (× base rate)</Label>
                <Input
                  id="overtime_multiplier"
                  type="number"
                  step="0.05"
                  min="1"
                  placeholder="1.5"
                  value={formData.overtime_multiplier}
                  onChange={(e) => setFormData({ ...formData, overtime_multiplier: e.target.value })}
                  data-testid="tech-overtime-multiplier-input"
                />
                <p className="text-xs text-slate-500">
                  Applied to overtime hours. Default 1.5× (time-and-a-half).
                </p>
              </div>
              </>
              )}
              <Button type="submit" className="w-full bg-slate-900 hover:bg-slate-800" data-testid="submit-technician-button">
                {editingTech ? 'Update Technician' : 'Add Technician'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        <div className="bg-white border border-slate-200 rounded-md shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="technicians-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Email</th>
                  {canSeeRates && (
                    <th className="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Cost/Hour</th>
                  )}
                  {canManage && (
                    <th className="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Actions</th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {technicians.map((tech) => (
                  <tr key={tech.id} className="hover:bg-slate-50" data-testid={`tech-row-${tech.id}`}>
                    <td className="px-6 py-4 text-sm font-medium text-slate-900">{tech.name}</td>
                    <td className="px-6 py-4 text-sm text-slate-600">{tech.email || 'N/A'}</td>
                    {canSeeRates && (
                      <td className="px-6 py-4 text-sm font-mono text-slate-900">${(tech.cost_per_hour ?? 0).toFixed(2)}</td>
                    )}
                    {canManage && (
                      <td className="px-6 py-4">
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openEditDialog(tech)}
                            data-testid={`edit-tech-${tech.id}`}
                          >
                            <Pencil size={16} />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDelete(tech.id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            data-testid={`delete-tech-${tech.id}`}
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
            {technicians.length === 0 && (
              <div className="p-12 text-center text-slate-600">
                No technicians found
              </div>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
